import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import Chatbot from '@/components/shared/Chatbot';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Building2, CheckCircle, Users, TrendingUp, GraduationCap, Award, 
  Phone, Banknote, BookOpen, Briefcase, Target, HeartHandshake,
  Monitor, BadgeCheck, Headphones, Megaphone, FileCheck, MapPin,
  ArrowRight, Star, Zap, Clock, ChevronRight, IndianRupee,
  Building, Laptop, Users2, Shield, Sparkles, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FranchisePage = () => {
  const navigate = useNavigate();
  const [showQuickForm, setShowQuickForm] = useState(false);
  const [quickFormData, setQuickFormData] = useState({
    name: '',
    mobile: '',
    city: '',
    investment_budget: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleQuickSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, {
        ...quickFormData,
        type: 'partner_enquiry',
        source: 'partner_page_quick_form'
      });
      toast.success('Enquiry submitted! Our team will contact you soon.');
      setShowQuickForm(false);
      setQuickFormData({ name: '', mobile: '', city: '', investment_budget: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    }
    setIsSubmitting(false);
  };

  // Earning Potential Cards
  const earningCards = [
    {
      icon: <Banknote className="w-10 h-10 text-green-500" />,
      title: "Monthly Income",
      value: "₹50,000 – ₹2,00,000",
      color: "green"
    },
    {
      icon: <TrendingUp className="w-10 h-10 text-blue-500" />,
      title: "Revenue Sources",
      value: "Govt + Courses + Distance + Apprenticeship + CSR",
      color: "blue"
    },
    {
      icon: <Target className="w-10 h-10 text-purple-500" />,
      title: "Low Investment",
      value: "Start with ₹50K – ₹2.5L",
      color: "purple"
    }
  ];

  // Income Streams
  const incomeStreams = [
    {
      icon: <Building2 className="w-8 h-8 text-blue-600" />,
      title: "Government Programs",
      points: ["PMKVY & State Schemes", "Regular batch allocations"]
    },
    {
      icon: <HeartHandshake className="w-8 h-8 text-pink-600" />,
      title: "CSR Projects",
      points: ["Corporate tie-ups", "Skill development funds"]
    },
    {
      icon: <Briefcase className="w-8 h-8 text-amber-600" />,
      title: "Apprenticeship (NAPS/NATS)",
      points: ["Industry partnerships", "Stipend-based training"]
    },
    {
      icon: <GraduationCap className="w-8 h-8 text-emerald-600" />,
      title: "D.Voc / B.Voc Programs",
      points: ["Degree programs", "University affiliations"]
    },
    {
      icon: <BookOpen className="w-8 h-8 text-indigo-600" />,
      title: "Distance Education",
      points: ["UG/PG degrees", "Flexible learning"]
    },
    {
      icon: <Award className="w-8 h-8 text-red-600" />,
      title: "Paid Skill Courses",
      points: ["Professional certifications", "Industry-ready skills"]
    }
  ];

  // Why Choose Iqra
  const whyChoose = [
    { icon: <BadgeCheck className="w-8 h-8" />, title: "Govt Affiliated", desc: "NSDC & Skill India partner" },
    { icon: <Headphones className="w-8 h-8" />, title: "Complete Setup Support", desc: "End-to-end assistance" },
    { icon: <Monitor className="w-8 h-8" />, title: "Ready Software", desc: "ERP + CRM included" },
    { icon: <Users className="w-8 h-8" />, title: "Placement Support", desc: "Job portal access" },
    { icon: <Megaphone className="w-8 h-8" />, title: "Marketing Assistance", desc: "Branding & leads" },
    { icon: <FileCheck className="w-8 h-8" />, title: "Training & Certification", desc: "Faculty training" }
  ];

  // What You Get
  const whatYouGet = [
    { icon: <Building className="w-6 h-6" />, text: "Center Branding Kit" },
    { icon: <Users2 className="w-6 h-6" />, text: "Student Management System" },
    { icon: <Laptop className="w-6 h-6" />, text: "LMS + Exam + Certificate" },
    { icon: <Briefcase className="w-6 h-6" />, text: "Job Placement Portal" },
    { icon: <Headphones className="w-6 h-6" />, text: "Dedicated Support Team" },
    { icon: <Shield className="w-6 h-6" />, text: "Legal & Compliance Help" }
  ];

  // Steps
  const steps = [
    { num: 1, title: "Apply for Franchise", icon: <FileCheck className="w-6 h-6" /> },
    { num: 2, title: "Get Approval", icon: <BadgeCheck className="w-6 h-6" /> },
    { num: 3, title: "Setup Center", icon: <Building className="w-6 h-6" /> },
    { num: 4, title: "Start Admissions", icon: <Users className="w-6 h-6" /> },
    { num: 5, title: "Earn Revenue", icon: <Banknote className="w-6 h-6" /> }
  ];

  // Affiliations
  const affiliations = [
    "Skill India", "NSDC", "MSDE", "ESSCI", "TSSCI", "NULM", "MSME", "Startup India"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* ==================== HERO SECTION ==================== */}
      <section className="relative bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text Content */}
            <div className="text-white space-y-6">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">
                <Sparkles className="w-4 h-4 text-yellow-300" />
                Limited Franchise Opportunities
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Start Your Own <br />
                <span className="text-yellow-300">Training Center</span><br />
                & Earn Monthly Income
              </h1>
              
              <p className="text-lg sm:text-xl text-blue-100 max-w-xl">
                Join Iqra Skills and build a profitable education business with government projects, 
                certification programs, and placement support.
              </p>

              {/* Badges */}
              <div className="flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 bg-green-500/90 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  <CheckCircle className="w-4 h-4" /> Govt Projects
                </span>
                <span className="inline-flex items-center gap-2 bg-amber-500/90 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  <CheckCircle className="w-4 h-4" /> Low Investment
                </span>
                <span className="inline-flex items-center gap-2 bg-purple-500/90 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  <CheckCircle className="w-4 h-4" /> High Returns
                </span>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 pt-4">
                <Button 
                  onClick={() => navigate('/partner-apply')}
                  className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 font-bold px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all"
                  data-testid="hero-apply-btn"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Apply for Franchise
                </Button>
                <Button 
                  onClick={() => window.open('tel:+919521484003')}
                  variant="outline"
                  className="bg-white/10 border-2 border-white text-white hover:bg-white hover:text-blue-700 font-bold px-8 py-6 text-lg rounded-xl backdrop-blur-sm transition-all"
                  data-testid="hero-call-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Talk to Expert
                </Button>
              </div>
            </div>

            {/* Right: Image */}
            <div className="relative hidden lg:block">
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=600&h=500&fit=crop" 
                  alt="Training Center" 
                  className="rounded-2xl shadow-2xl"
                />
                {/* Floating Card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 animate-bounce">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <IndianRupee className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Avg. Monthly Earning</p>
                      <p className="text-xl font-bold text-green-600">₹1,00,000+</p>
                    </div>
                  </div>
                </div>
                {/* Floating Card 2 */}
                <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-xl p-3">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    <span className="font-bold">500+ Partners</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== EARNING POTENTIAL ==================== */}
      <section className="py-16 bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              💰 Earning Potential
            </h2>
            <p className="text-green-400 text-xl font-semibold animate-pulse">
              "Start earning from Day 1"
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {earningCards.map((card, idx) => (
              <div 
                key={idx}
                className={`bg-gradient-to-br from-${card.color}-500/20 to-${card.color}-600/10 border border-${card.color}-500/30 rounded-2xl p-8 text-center hover:scale-105 transition-transform duration-300`}
              >
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 rounded-full mb-6">
                  {card.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-300 mb-2">{card.title}</h3>
                <p className="text-2xl sm:text-3xl font-bold text-white">{card.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== MULTIPLE INCOME STREAMS ==================== */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Multiple Income Opportunities
            </h2>
            <p className="text-gray-600 text-lg">Earn from multiple sources simultaneously</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {incomeStreams.map((stream, idx) => (
              <div 
                key={idx}
                className="group bg-gray-50 hover:bg-white border border-gray-200 hover:border-blue-500 hover:shadow-xl rounded-2xl p-6 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-white group-hover:bg-blue-50 rounded-xl shadow-sm transition-colors">
                    {stream.icon}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">{stream.title}</h3>
                    <ul className="space-y-1">
                      {stream.points.map((point, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-6 py-3 rounded-full font-semibold">
              <Sparkles className="w-5 h-5" />
              Earn from multiple sources with single investment
            </p>
          </div>
        </div>
      </section>

      {/* ==================== WHY CHOOSE IQRA ==================== */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Partner With Iqra Skills?
            </h2>
            <p className="text-gray-600 text-lg">Trust built over 15+ years of excellence</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-blue-500"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                    {item.icon}
                  </div>
                  <h3 className="font-bold text-gray-900">{item.title}</h3>
                </div>
                <p className="text-gray-600 ml-14">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== WHAT YOU GET ==================== */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                What You Get as a Partner
              </h2>
              <p className="text-gray-600 mb-8">
                Everything you need to run a successful training center
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {whatYouGet.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-xl p-4">
                    <div className="p-2 bg-green-100 text-green-600 rounded-lg">
                      {item.icon}
                    </div>
                    <span className="font-medium text-gray-800">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop"
                alt="Partner Benefits"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How to Start
            </h2>
            <p className="text-gray-400 text-lg">5 simple steps to launch your training center</p>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-4 lg:gap-2">
            {steps.map((step, idx) => (
              <React.Fragment key={idx}>
                <div className="flex flex-col items-center text-center w-32 sm:w-40">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white shadow-lg mb-3">
                    {step.icon}
                  </div>
                  <div className="w-8 h-8 bg-yellow-400 text-gray-900 rounded-full flex items-center justify-center font-bold text-sm mb-2">
                    {step.num}
                  </div>
                  <p className="text-white font-medium text-sm">{step.title}</p>
                </div>
                {idx < steps.length - 1 && (
                  <ChevronRight className="hidden lg:block w-8 h-8 text-gray-600" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== INVESTMENT SECTION ==================== */}
      <section className="py-16 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                💸 Investment Details
              </h2>
              
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-4 bg-green-100 rounded-xl">
                    <IndianRupee className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Investment Range</p>
                    <p className="text-3xl font-bold text-green-600">₹50,000 – ₹2,50,000</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl p-4 text-center">
                  <p className="font-semibold">✨ Low Risk, High Growth Business</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">📋 Requirements</h3>
              
              <div className="bg-white rounded-2xl shadow-xl p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Building className="w-5 h-5 text-blue-500" />
                    <span className="text-sm">Office Space</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Laptop className="w-5 h-5 text-blue-500" />
                    <span className="text-sm">Computer Lab (300 Sq Ft)</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Users className="w-5 h-5 text-blue-500" />
                    <span className="text-sm">Classroom (300 Sq Ft)</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <MapPin className="w-5 h-5 text-blue-500" />
                    <span className="text-sm">Reception Area</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <BookOpen className="w-5 h-5 text-blue-500" />
                    <span className="text-sm">Library Area</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Users2 className="w-5 h-5 text-blue-500" />
                    <span className="text-sm">Counseling Area</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Monitor className="w-5 h-5 text-blue-500" />
                    <span className="text-sm">Projector in Classroom</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Shield className="w-5 h-5 text-blue-500" />
                    <span className="text-sm">CCTV Cameras</span>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
                  <p className="text-amber-800 text-sm font-medium">
                    📌 <strong>Training Center (TC):</strong> Minimum 2000 Sq Ft Area<br />
                    📌 <strong>District Level Center (DLC):</strong> Minimum 3000 Sq Ft Area<br />
                    📌 <strong>State Partner (SP):</strong> Minimum 5000 Sq Ft Area Required
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== AFFILIATIONS ==================== */}
      <section className="py-12 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Recognized & Affiliated
            </h2>
            <p className="text-gray-500">Trusted by Government & Industry</p>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-8">
            {affiliations.map((name, idx) => (
              <div 
                key={idx}
                className="bg-gray-100 hover:bg-blue-100 px-6 py-3 rounded-full transition-colors"
              >
                <span className="font-semibold text-gray-700">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 relative overflow-hidden">
        {/* Background Animation */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-72 h-72 bg-yellow-400 rounded-full filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-400 rounded-full filter blur-3xl animate-pulse" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Start Your Own<br />Training Center?
          </h2>
          <p className="text-xl text-blue-100 mb-4">
            Limited franchise opportunities available in your area.
          </p>
          <p className="inline-flex items-center gap-2 bg-red-500/80 text-white px-6 py-2 rounded-full text-sm font-bold mb-8 animate-pulse">
            <Clock className="w-4 h-4" />
            Limited Seats Available – Apply Now!
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => navigate('/partner-apply')}
              className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-gray-900 font-bold px-10 py-6 text-lg rounded-xl shadow-2xl hover:shadow-yellow-500/25 transform hover:-translate-y-1 transition-all"
              data-testid="final-apply-btn"
            >
              <Zap className="w-5 h-5 mr-2" />
              Apply Now
            </Button>
            <Button 
              onClick={() => window.open('tel:+919521484003')}
              variant="outline"
              className="bg-white/10 border-2 border-white text-white hover:bg-white hover:text-purple-700 font-bold px-10 py-6 text-lg rounded-xl backdrop-blur-sm transition-all"
              data-testid="final-call-btn"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call Now
            </Button>
          </div>
        </div>
      </section>

      {/* ==================== STICKY BOTTOM FORM ==================== */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-2xl z-50 transform transition-transform duration-300">
        <div className="max-w-7xl mx-auto px-4 py-3">
          {!showQuickForm ? (
            <div className="flex items-center justify-between">
              <div className="hidden sm:block">
                <p className="font-bold text-gray-900">Want to become a partner?</p>
                <p className="text-sm text-gray-500">Get callback from our team</p>
              </div>
              <Button 
                onClick={() => setShowQuickForm(true)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold px-8 py-3 rounded-xl w-full sm:w-auto"
              >
                <ArrowRight className="w-5 h-5 mr-2" />
                Quick Enquiry
              </Button>
            </div>
          ) : (
            <form onSubmit={handleQuickSubmit} className="relative">
              <button 
                type="button"
                onClick={() => setShowQuickForm(false)}
                className="absolute -top-2 right-0 p-1 bg-gray-100 rounded-full hover:bg-gray-200"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <Input
                  placeholder="Your Name"
                  value={quickFormData.name}
                  onChange={(e) => setQuickFormData({...quickFormData, name: e.target.value})}
                  required
                  className="col-span-1"
                />
                <Input
                  placeholder="Mobile Number"
                  value={quickFormData.mobile}
                  onChange={(e) => setQuickFormData({...quickFormData, mobile: e.target.value})}
                  required
                  className="col-span-1"
                />
                <Input
                  placeholder="City"
                  value={quickFormData.city}
                  onChange={(e) => setQuickFormData({...quickFormData, city: e.target.value})}
                  required
                  className="col-span-1"
                />
                <select
                  value={quickFormData.investment_budget}
                  onChange={(e) => setQuickFormData({...quickFormData, investment_budget: e.target.value})}
                  required
                  className="col-span-1 px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">Budget</option>
                  <option value="50000">₹50,000</option>
                  <option value="150000">₹1,50,000</option>
                  <option value="500000">₹5,00,000</option>
                </select>
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="col-span-2 sm:col-span-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold rounded-xl"
                >
                  {isSubmitting ? 'Sending...' : 'Apply Now →'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Spacer for sticky form */}
      <div className="h-20" />

      <Footer />
      <Chatbot />
    </div>
  );
};

export default FranchisePage;
